import { DangerAlert } from "components/common/Alerts";
import { Breadcrumb, BreadcrumbLink } from "components/common/Breadcrumb";
import ContentLoading from "components/common/ContentLoading";
import Disclaimer from "interfaces/Disclaimer";
import QuoteSalesOrder from "interfaces/QuoteSalesOrder";
import Vendor from "interfaces/Vendor";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apifetch from "utils/apifetch";
import extractErrorMessage from "utils/extractErrorMessage";
import { MsalContext } from "../common/msal";
import SaleOrderAlert from "./SaleOrderAlert";
import SalesOrderStep1 from "./SalesOrderStep1";
import SalesOrderStep2 from "./SalesOrderStep2";
import SalesOrderStep3 from "./SalesOrderStep3";
import SalesOrderStep4 from "./SalesOrderStep4";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

interface RouteParams {
    quoteId: number;
    orderId?: number;
}

interface State {
    isLoading: boolean;
    hasValidationErrors: boolean;
    errorMessage?: string;
    stepNumber: number;
    dataIsloading: boolean;
    isDraft: boolean;
    data: ISalesOrder;
    disclaimers: Disclaimer[];
    isDirectSalesOrder: boolean;
    vendors: Vendor[];
}

interface ISalesOrder extends QuoteSalesOrder {
    parentQuoteId?: number;
}

export default class SalesOrder extends React.Component<
    RouteComponentProps<RouteParams>,
    State
> {
    static contextType = MsalContext;
    context!: React.ContextType<typeof MsalContext>;

    constructor(props: RouteComponentProps<RouteParams>) {
        super(props);

        this.state = {
            dataIsloading: false,
            isLoading: true,
            hasValidationErrors: false,
            errorMessage: undefined,
            stepNumber: 1,
            isDraft: false,
            disclaimers: [],
            data: {
                id: 0,
                quoteId: 0,
                quoteName: "",
                jobId: 0,
                jobName: "",
                date: "",
                expirationDate: "",
                isMaster: false,
                isCashSale: false,
                salespersonName: "",
                preparerName: "",
                quoteStatusId: 0,
                fobId: 1,
                price: 0,
                isDraft: false,
                customers: [],
                productGroups: [],
                disclaimers: [],
                quoteHistories: [],
                costGroups: [],
                isSalesOrder: true,
                externalSalesOrderId: "",
                salesOrderNumber: "",
                salesOrderUrl: "",
                externalPurchaseOrderId: "",
                purchaseOrderNumber: "",
                jobCustomerAddress: {
                    id: 0,
                    jobId: 0,
                    jobName: "",
                    customerId: 0,
                    customerName: "",
                },
                defaultSalesTaxRate: 0,
                isTaxable: false,
                cityStateZip: [],
                authToken: "",
                lookupAddresses: [],
                customerId: 0,
                customerContactName: "",
                customerPhone: "",
                jobCustomerAddresses: [],
                isYardPickup: true,
            },
            isDirectSalesOrder: false,
            vendors: [],
        };

        this.handleSalesOrderStep1Submit =
            this.handleSalesOrderStep1Submit.bind(this);
        this.handleSalesOrderStep2Submit =
            this.handleSalesOrderStep2Submit.bind(this);
        this.handleSalesOrderStep3Submit =
            this.handleSalesOrderStep3Submit.bind(this);
        this.handleStep4Submit = this.handleStep4Submit.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    public render() {
        if (this.state.isLoading) {
            return (
                <div className="container">
                    <ContentLoading topOffset={350} />
                </div>
            );
        }

        let step = null;
        switch (this.state.stepNumber) {
            case 1:
                step = (
                    <SalesOrderStep1
                        onSubmit={this.handleSalesOrderStep1Submit}
                        onCancel={this.handleCancel}
                        isDraft={this.state.isDraft}
                        data={this.state.data}
                        defaultSalesTaxRate={this.state.data.defaultSalesTaxRate}
                    />
                );
                break;
            case 2:
                step = (
                    <SalesOrderStep2
                        onSubmit={this.handleSalesOrderStep2Submit}
                        onCancel={this.handleCancel}
                        isDraft={this.state.isDraft}
                        data={this.state.data}
                    />
                );
                break;
            case 3:
                step = (
                    <SalesOrderStep3
                        onSubmit={this.handleSalesOrderStep3Submit}
                        onCancel={this.handleCancel}
                        data={this.state.data}
                        vendors={this.state.vendors}
                    />
                );
                break;
            case 4:
                step = (
                    <SalesOrderStep4
                        isLoading={this.state.dataIsloading}
                        isError={this.state.hasValidationErrors}
                        onSubmit={this.handleStep4Submit}
                        onCancel={this.handleCancel}
                        isNewQuote={false}
                        isDraft={this.state.isDraft}
                        disclaimers={this.state.disclaimers}
                        selectedDisclaimers={this.state.data.disclaimers}
                        data={this.state.data}
                    />
                );
                break;
        }

        return (
            <div className="container" style={{ marginBottom: "100px" }}>
                <div className="animated fadeIn">
                    <Breadcrumb>
                        <BreadcrumbLink to="/">Home</BreadcrumbLink>
                        <BreadcrumbLink to="/quotes">Quotes</BreadcrumbLink>
                        <BreadcrumbLink isActive={true}>
                            Quote to Sales Order
                        </BreadcrumbLink>
                    </Breadcrumb>
                    <SaleOrderAlert />
                    {this.state.errorMessage && (
                        <DangerAlert name="dangerAlert">{this.state.errorMessage}</DangerAlert>
                    )}
                    <ToastContainer position="bottom-right" />
                    {step}
                </div>
            </div>
        );
    }

    public async componentDidMount() {

        if (this.state.isLoading) {
            let response = await apifetch(
                "quotes/salesorder/" +
                this.props.match.params.quoteId +
                "/" +
                this.props.match.params.orderId,
                undefined,
                true
            );


            if (response.ok) {
                let data = (await response.json()) as QuoteSalesOrder;
                data.quoteId = data.id;
                //Change id to 0, as this is order
                data.id = this.props.match.params.orderId
                    ? this.props.match.params.orderId
                    : 0;

                if (data.id <= 0) {
                    data.isTaxable = data.salesTaxRate != null;
                }

                this.setState({
                    isLoading: false,
                    isDraft: data.isDraft,
                    data: data,
                });

                let resVendors = await apifetch("vendors/getall", undefined, true);

                if (resVendors.ok) {
                    let vendorData = (await resVendors.json()) as Vendor[];

                    this.setState({
                        vendors: vendorData,
                    });
                } else {
                    const errorText = await extractErrorMessage(resVendors);
                    this.setState({ errorMessage: errorText });
                }
                document.title = `Quote to Sales order for ${this.state.data.jobName} - Quotes - ${SITE_NAME}`;
            } else {
                const errorText = await extractErrorMessage(response);
                this.setState({
                    isLoading: false,
                    errorMessage: errorText,
                });
            }
        }
    }

    private handleSalesOrderStep1Submit(data: any) {
        let stateData = this.state.data;

        stateData.quoteName = data.quoteName;
        stateData.jobId = data.jobId;
        stateData.jobName = data.jobName;
        stateData.jobAddress = data.jobAddress;
        stateData.jobCity = data.jobCity;
        stateData.jobState = data.jobState;
        stateData.jobZip = data.jobZip;
        //todo: iterate?
        stateData.customers[0].customerName = data.customerName;
        stateData.customers[0].customerContactName = data.customerContactName;
        stateData.customers[0].phone = data.phone;
        stateData.customers[0].city = data.city;

        stateData.shippingMethod = data.shippingMethod;
        stateData.shippingMethodYard = data.shippingMethodYard;
        stateData.shippingAddress = data.shippingAddress;
        stateData.shippingStreetAddress = data.shippingStreetAddress;
        stateData.shippingCity = data.shippingCity;
        stateData.shippingState = data.shippingState;
        stateData.shippingZip = data.shippingZip;
        stateData.shippingDate = data.shippingDate;
        stateData.projectNote = data.projectNote;
        stateData.salesTax = data.salesTax1;
        stateData.salesTaxRate = data.salesTaxRate1;
        stateData.cityStateZip = data.cityStateZip;
        //stateData.shippingSalesTaxRate = data.shippingSalesTaxRate;
        stateData.isTaxable = data.isTaxable;
        stateData.salespersonCode = data.salespersonCode;
        stateData.salespersonName = data.salespersonName;

        stateData.isYardPickup = data.isYardPickup;

        this.setState({
            data: stateData,
            stepNumber: 2,
        });

    }

    private handleSalesOrderStep2Submit(data: any) {
        let stateData = this.state.data;

        stateData.productGroups = data.categories;

        this.setState({
            data: stateData,
            stepNumber: data.nextStepNumber,
        });

    }

    private handleSalesOrderStep3Submit(data: any) {
        let stateData = this.state.data;

        stateData.costGroups = data.categories;

        stateData.selectedVendorId = data.selectedVendorId;
        stateData.selectedVendorNo = data.selectedVendorNo;
        stateData.selectedVendorName = data.selectedVendorName;

        this.setState({
            data: stateData,
            stepNumber: data.nextStepNumber,
        });

    }

    private async handleStep4Submit(
        data: any
    ) {
        let stateData = this.state.data;

        if (data.nextStepNumber !== undefined) {
            this.setState({
                stepNumber: data.nextStepNumber,
            });
        } else {
            if (data.saveAsNewOrder) {
                stateData.isDraft = false;

                this.setState({
                    data: stateData,
                });

                this.handleSubmit();
            } else if (data.saveAsDraft) {
                stateData.isDraft = true;

                this.setState({
                    data: stateData,
                });

                this.handleSubmit();
            }
        }
    }

    private async handleSubmit() {
        let stateData = this.state.data;
        stateData.parentQuoteId = this.props.match.params.quoteId;
        stateData.shippingSalesTaxRate = stateData.salesTaxRate;

        this.setState({ dataIsloading: true });

        let config = require(`../../config.${HostingEnv}.ts`) as any;
        const accounts = this.context.accounts;

        if (accounts.length > 0) {

            const scopeBc = config.authRequestBc.scopes[0];

            const account = this.context.accounts[0];
            if (account) {
                const request = {
                    scopes: [scopeBc],
                    account: account,
                    interactionType: 'Silent'
                };
                const instance = this.context.instance;
                instance
                    .acquireTokenSilent(request)
                    .then(async (resp) => {

                        if (resp) {
                            stateData.authToken = resp.accessToken;

                            let response = await apifetch(
                                "quotes/salesorder",
                                {
                                    headers: new Headers({
                                        Accept: "application/json",
                                        "Content-Type": "application/json",
                                    }),
                                    method: "POST",
                                    body: JSON.stringify(stateData),
                                },
                                true
                            );
                            if (response.ok) {
                                let responseData = await response.json();
                                if (
                                    responseData &&
                                    responseData.parentQuoteId &&
                                    responseData.parentQuoteId > 0
                                ) {
                                    this.setState({ dataIsloading: false, errorMessage: undefined });
                                    const scrollData = { scrollToBottom: 'true' };
                                    this.props.history.push({
                                        pathname: "/quotes/" + responseData.parentQuoteId,
                                        state: { data: scrollData }
                                    }
                                    );
                                }
                            } else {
                                const errorText = await extractErrorMessage(response);
                                this.setState({
                                    hasValidationErrors: true,
                                    dataIsloading: false,
                                    errorMessage: errorText,
                                });

                                toast.error(errorText || 'Unable to process order.', {
                                    autoClose: false,
                                });
                            }
                        }
                        else {
                            let response = await apifetch(
                                "quotes/salesorder",
                                {
                                    headers: new Headers({
                                        Accept: "application/json",
                                        "Content-Type": "application/json",
                                    }),
                                    method: "POST",
                                    body: JSON.stringify(stateData),
                                },
                                true
                            );
                            if (response.ok) {
                                let responseData = await response.json();
                                if (
                                    responseData &&
                                    responseData.parentQuoteId &&
                                    responseData.parentQuoteId > 0
                                ) {
                                    this.setState({ dataIsloading: false, errorMessage: undefined });
                                    const scrollData = { scrollToBottom: 'true' };
                                    this.props.history.push({
                                        pathname: "/quotes/" + responseData.parentQuoteId,
                                        state: { data: scrollData }
                                    }
                                    );
                                }
                            } else {
                                const errorText = await extractErrorMessage(response);
                                this.setState({
                                    hasValidationErrors: true,
                                    dataIsloading: false,
                                    errorMessage: errorText,
                                });

                                toast.error(errorText || 'Unable to process order.', {
                                    autoClose: false,
                                });
                            }
                        }
                    })
                    .catch(async (err) => {

                        //console.log("SO: Silent token acquisition failed, acquiring token interactively...", err);

                        if (err instanceof InteractionRequiredAuthError) {

                            const resp = await instance.acquireTokenPopup(request);
                            stateData.authToken = resp.accessToken;
                            let response = await apifetch(
                                "quotes/salesorder",
                                {
                                    headers: new Headers({
                                        Accept: "application/json",
                                        "Content-Type": "application/json",
                                    }),
                                    method: "POST",
                                    body: JSON.stringify(stateData),
                                },
                                true
                            );
                            if (response.ok) {
                                let responseData = await response.json();
                                if (
                                    responseData &&
                                    responseData.parentQuoteId &&
                                    responseData.parentQuoteId > 0
                                ) {
                                    this.setState({ dataIsloading: false, errorMessage: undefined });
                                    const scrollData = { scrollToBottom: 'true' };
                                    this.props.history.push({
                                        pathname: "/quotes/" + responseData.parentQuoteId,
                                        state: { data: scrollData }
                                    }
                                    );
                                }
                            } else {
                                const errorText = await extractErrorMessage(response);
                                this.setState({
                                    hasValidationErrors: true,
                                    dataIsloading: false,
                                    errorMessage: errorText,
                                });

                                toast.error(errorText || 'Unable to process order.', {
                                    autoClose: false,
                                });
                            }
                        }

                        //await instance.loginRedirect({
                        //    scopes: [scopeBc],
                        //    account: account
                        //});
                    });
            }
        }

    }

    private handleCancel() {
        this.props.history.push("/quotes/" + this.props.match.params.quoteId);
    }
}



// WEBPACK FOOTER //
// ./ClientApp/components/quotes/SalesOrder.tsx